import { deriveOriginLink } from '~support-at-home/lib/derive';

import { faEdit } from '~components/icon';
import { Column, Row } from '~components/table-view-v3';
import { selectEditor } from '~components/table-view-v3/columnEditors';
import { linkCellRender } from '~components/table-view-v3/columnRenderers';
import ClaimItemActions from '~support-at-home/components/claims/ClaimItemActions';
import ClaimValidIcon from '~support-at-home/components/claims/ClaimValidIcon';

import { SahService, SahServiceItem, SahServiceWrap } from '~lookups/models';

import BudgetIcon from '~support-at-home/forms/supplier-invoices/BudgetIcon';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

export const claimItemColumnsFactory = (
  sahServices: SahService[],
  serviceTypes: ServiceTypeRow[],
  sahServiceWraps: SahServiceWrap[],
  sahServiceItems: SahServiceItem[],
  onIgnore?: (id: string) => void,
): Column[] => {
  const wrapOptions = sahServiceWraps.map(({ id, description, service_id }) => ({ id, name: description, service_id }));
  const itemOptions = sahServiceItems.map(({ id, description, service_id }) => ({ id, name: description, service_id }));

  const columns: Column[] = [
    {
      id: 'reference',
      header: 'Ref.',
      type: 'text',
      isEditable: false,
    },
    {
      id: 'origin_tag',
      header: 'Origin',
      type: 'text',
      renderReadOnly: linkCellRender(
        (row) => window.open(deriveOriginLink(row), '_blank', 'noopener,noreferrer'),
        faEdit,
        {
          classNames: {
            cell: 'px-1',
          },
        } as any,
      ),
      isEditable: false,
    },
    {
      id: 'client_id',
      header: 'Client Name',
      type: 'text',
      deriveValue: (row) => row.client?.full_name || '',
      isEditable: false,
    },
    {
      id: 'service_date',
      header: 'Date',
      type: 'date',
      isEditable: false,
    },
    {
      id: 'sah_service_id',
      header: 'SaH Service',
      type: 'text',
      deriveValue: (row) => {
        const service = sahServices.find((s) => s.id === row.sah_service_id);
        return service ? service.name : '';
      },
      isEditable: false,
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'text',
      deriveValue: (row) => {
        const serviceType = serviceTypes.find((s) => s.id === row.service_type_id);
        return serviceType ? serviceType.name : '-';
      },
      isEditable: false,
    },
    {
      id: 'sah_wrap_id',
      header: 'SaH Wrap',
      type: 'select',
      options: wrapOptions,
      renderEditor: selectEditor(
        (row) => {
          const filteredWraps = wrapOptions.filter((wrap) => wrap.service_id === row.sah_service_id);
          return filteredWraps;
        },
        true,
        undefined,
        true,
      ),
      isEditable: (row) => !row.statement?.id,
    },
    {
      id: 'sah_item_id',
      header: 'SaH Item',
      type: 'select',
      options: itemOptions,
      renderEditor: selectEditor(
        (row) => {
          const filteredItems = itemOptions.filter((item) => item.service_id === row.sah_service_id);
          return filteredItems;
        },
        true,
        undefined,
        true,
      ),
      isEditable: (row) => !row.statement?.id,
    },
    {
      id: 'claimed_total',
      header: 'Amount',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      deriveValue: (row) => {
        const total = Number(row.units) * Number(row.unit_amount);
        return total > 0 ? `$${total.toFixed(2)}` : '';
      },
      isEditable: false,
    },
    {
      id: 'client_budget_id',
      header: 'Budget',
      type: 'custom',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: '50px' },
      component: BudgetIcon,
      isEditable: false,
    },
    {
      id: 'is_valid',
      header: 'Valid',
      type: 'custom',
      classNames: { column: 'cell-align-center' },
      component: ClaimValidIcon,
      componentProps: (row: Row) => ({
        id: row.id,
        row,
      }),
      isEditable: false,
    },
    {
      id: 'error_message',
      header: 'Error',
      type: 'text',
      deriveValue: (row) => row.error?.message ?? '',
      isEditable: false,
    },
    {
      id: 'error_link',
      header: 'Investigate',
      type: 'text',
      deriveValue: (row) => (row.error?.link ? 'View' : null),
      renderReadOnly: linkCellRender((row) => window.open(row.error?.link, '_blank', 'noopener,noreferrer'), faEdit, {
        classNames: {
          cell: 'px-1',
        },
      } as any),
      isEditable: false,
    },
    {
      id: 'actions',
      header: '',
      type: 'custom',
      classNames: {
        cell: 'border-0',
      },
      component: ClaimItemActions,
      componentProps: (row: Row) => ({
        id: row.id,
        isIgnored: row.is_ignored,
        onIgnore,
        isLocked: !!row.statement?.id,
      }),
    },
  ];

  return columns;
};
